import { motion } from "framer-motion";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { usePlatform } from "@/hooks/usePlatform";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { OnboardingBackButton } from "@/modules/onboarding/components/OnboardingBackButton/OnboardingBackButton";
import {
  SourceForm,
  type SourceFormData,
} from "@/modules/onboarding/forms/SourceForm/SourceForm";
import { updateUserIfAuthenticated } from "@/modules/onboarding/utils/updateUserIfAuthenticated";
import { LocalStorageService } from "@/modules/storage";
import { useUser } from "@/modules/user/useUser/useUser";

const V3SignupSourcePage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { platform } = usePlatform();

  const referralSenderName = LocalStorageService.getItem("referralSenderName");
  const initialSource = referralSenderName
    ? "friend"
    : user?.signUpSource ?? "";
  const initialReferrerName = referralSenderName
    ? referralSenderName
    : user?.referrerName ?? "";
  const isDisabled = user?.hasBeenPreRegistered && !!user.signUpSource;
  const employeeNameReferralEnabled =
    platform?.platformSettings?.employeeNameReferralEnabled ?? false;

  const handleSubmit = useCallback(
    async (data: SourceFormData) => {
      await updateUserIfAuthenticated({
        signUpSource: data.signUpSource,
        referrerName:
          data.signUpSource === "employee" ||
          data.signUpSource === "other" ||
          data.signUpSource === "friend"
            ? data.referrerName
            : null,
      });

      // If coming from gift card claim flow, show gift card redeem animation
      const isGiftCardClaim =
        LocalStorageService.getItem("isGiftCardClaim") === "true";
      if (isGiftCardClaim) {
        navigate("/signin/gift-card-redeem");
      } else {
        navigate("/signin/rewards");
      }
    },
    [navigate]
  );

  return (
    <MobileViewportContainer
      header={<OnboardingBackButton prev="/signin/gender" />}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
        className="flex w-full max-w-sm flex-col items-center"
      >
        <SourceForm
          key={`source-${initialSource}-${initialReferrerName}`}
          initialSource={initialSource}
          initialReferrerName={initialReferrerName}
          onSubmit={handleSubmit}
          disabled={isDisabled}
          employeeNameReferralEnabled={employeeNameReferralEnabled}
          submitButtonText="Confirm"
        />
      </motion.div>
    </MobileViewportContainer>
  );
};

export default V3SignupSourcePage;
